import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Socket } from 'socket.io-client';
import {
  LIVE_CLIENT_EVENTS,
  LIVE_EVENTS,
  LIVE_FALLBACK_AFTER_SECONDS,
  LIVE_PATH,
  TRACK_POLL_SECONDS,
  liveRooms,
  type LiveAck,
  type OrderMessage,
  type OrderMessageList,
  type OrderTrack,
  type RiderLocationEvent,
} from '@capsico/contracts';
import { api, errorStatus } from '../api/client';
import { fetchTrack } from '../api/tracking';
import {
  applyRiderLocation,
  applyStatus,
  isMoving,
  liveMode,
  mergeMessage,
  type LiveMode,
} from './liveOrderMerge';

export const trackKey = (orderId: string) => ['order-track', orderId] as const;
export const messagesKey = (orderId: string) => ['order-messages', orderId] as const;

/** The API origin (the socket lives on the API server under LIVE_PATH, not under a namespace). */
function liveOrigin(): string {
  const base = api.defaults.baseURL ?? window.location.origin;
  try {
    return new URL(base, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
}

/**
 * Live order tracking (W14 §2). One cached OrderTrack per order, fed by:
 *  - the socket (`/v1/live`, the session cookie authenticates it like the REST client), joined
 *    to `order:<id>` — `order.status`, `order.rider_location` and `order.message` are merged in
 *    with the pure rules in liveOrderMerge;
 *  - a 15 s poll of GET …/track, only while the socket has been down for more than 10 s.
 * The socket runs only while the order still moves; finished orders stop everything.
 * When tracking is switched off on the server (404), `available` is false and the order page
 * falls back to its plain status card.
 */
export function useLiveOrder(orderId: string) {
  const client = useQueryClient();
  const [connection, setConnection] = useState<{ connected: boolean; downSince: number | null }>({
    connected: false,
    downSince: null,
  });
  const [now, setNow] = useState(() => Date.now());

  const query = useQuery({
    queryKey: trackKey(orderId),
    queryFn: async () => {
      const fresh = await fetchTrack(orderId);
      return applyStatus(client.getQueryData<OrderTrack>(trackKey(orderId)), fresh);
    },
    enabled: orderId !== '',
    staleTime: 10_000,
    retry: (count, error) => errorStatus(error) !== 404 && count < 2,
    refetchOnWindowFocus: true,
  });

  const track = query.data ?? null;
  const moving = isMoving(track);
  const mode: LiveMode = liveMode(connection, now, LIVE_FALLBACK_AFTER_SECONDS);
  const polling = moving && mode === 'polling';

  // Polling fallback: exactly one poll loop, only while the socket is down for > 10 s.
  useEffect(() => {
    if (!polling) return;
    const timer = window.setInterval(() => {
      void client.invalidateQueries({ queryKey: trackKey(orderId), exact: true });
    }, TRACK_POLL_SECONDS * 1000);
    return () => window.clearInterval(timer);
  }, [polling, client, orderId]);

  // While the socket is down, tick once a second so the 10 s threshold is noticed.
  useEffect(() => {
    if (!moving || connection.connected || connection.downSince === null) return;
    if (mode === 'polling') return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [moving, connection.connected, connection.downSince, mode]);

  // The socket: one per order page, only while the order moves.
  const lastStatus = useRef<string | null>(null);
  useEffect(() => {
    if (!orderId || !moving) return;
    const room = liveRooms.order(orderId);
    let socket: Socket | null = null;
    let cancelled = false;

    const markDown = () => {
      const at = Date.now();
      setNow(at);
      setConnection((current) =>
        current.connected || current.downSince === null
          ? { connected: false, downSince: at }
          : current,
      );
    };

    // Start the "down" clock now: if the first connect never succeeds we still fall back.
    markDown();

    // socket.io-client loads only on a live order page, so other screens never pay for it.
    void import('socket.io-client')
      .then(({ io }) => {
        if (cancelled) return;
        const live = io(liveOrigin(), {
          path: LIVE_PATH,
          withCredentials: true,
          transports: ['websocket', 'polling'],
          reconnectionDelay: 1000,
          reconnectionDelayMax: 10_000,
        });
        socket = live;

        live.on('connect', () => {
          live.emit(LIVE_CLIENT_EVENTS.subscribe, { room }, (ack: LiveAck | undefined) => {
            if (ack?.ok) {
              setConnection({ connected: true, downSince: null });
              // Catch up on anything missed while the socket was away.
              void client.invalidateQueries({ queryKey: trackKey(orderId), exact: true });
            } else {
              // Reached the server but not the room (e.g. forbidden): treat as down → polling.
              markDown();
            }
          });
        });
        live.on('disconnect', markDown);
        live.on('connect_error', markDown);

        live.on(LIVE_EVENTS.orderStatus, (payload: OrderTrack) => {
          if (!payload || payload.orderId !== orderId) return;
          client.setQueryData<OrderTrack>(trackKey(orderId), (previous) =>
            applyStatus(previous, payload),
          );
        });
        live.on(LIVE_EVENTS.riderLocation, (payload: RiderLocationEvent) => {
          client.setQueryData<OrderTrack | undefined>(trackKey(orderId), (previous) =>
            applyRiderLocation(previous, payload),
          );
        });
        live.on(LIVE_EVENTS.orderMessage, (payload: OrderMessage) => {
          if (!payload?.id) return;
          client.setQueryData<OrderMessageList | undefined>(messagesKey(orderId), (previous) =>
            mergeMessage(previous, payload),
          );
        });
      })
      .catch(() => {
        // The socket chunk failed to load (offline): the 15 s poll covers it.
        markDown();
      });

    return () => {
      cancelled = true;
      if (socket) {
        socket.emit(LIVE_CLIENT_EVENTS.unsubscribe, { room });
        socket.removeAllListeners();
        socket.disconnect();
      }
      setConnection({ connected: false, downSince: null });
    };
  }, [orderId, moving, client]);

  // A new status (from the socket or a poll) can change the bill, payment or refund lines:
  // refresh the order itself once per real change — never on a timer.
  const status = track?.status ?? null;
  useEffect(() => {
    if (status === null) return;
    if (lastStatus.current !== null && lastStatus.current !== status) {
      void client.invalidateQueries({ queryKey: ['my-order', orderId], exact: true });
      void client.invalidateQueries({ queryKey: ['my-orders'] });
    }
    lastStatus.current = status;
  }, [status, client, orderId]);

  return {
    loading: query.isLoading,
    /** False when the server has tracking switched off (404) or the track failed to load. */
    available: track !== null,
    unavailable: errorStatus(query.error) === 404,
    track,
    moving,
    mode: moving ? mode : ('live' as LiveMode),
    /** Show the "Reconnecting… updating every 15 s" banner. */
    reconnecting: polling,
    retry: () => void query.refetch(),
  };
}
